<template>
  <transition name="cs-fade">
    <div
      v-if="visible"
      class="cs-root"
      :class="{ 'cs-root--mobile': !isPc, 'cs-root--pc': isPc }"
      @click="onRootClick"
    >
      <div class="cs-panel" @click.stop>
        <div v-if="!isPc" class="sheet-handle" aria-hidden="true"></div>
        <header class="cs-header">
          <span class="cs-title">{{ $t('customerService.title') }}</span>
          <button
            v-if="isPc"
            type="button"
            class="cs-close"
            @click="close"
            :aria-label="$t('account.cancel')"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </header>
        <div ref="messageList" class="cs-messages">
          <LdSkeleton v-if="pageLoading" variant="chat" :count="5" />
          <template v-else>
          <div v-if="loadingMore" class="cs-loading">{{ $t('customerService.loading') }}</div>
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="cs-msg-row"
            :class="msg.senderType === 1 ? 'cs-msg-row--mine' : 'cs-msg-row--other'"
          >
            <div class="cs-bubble">
              <img
                v-if="msg.msgType === 'image'"
                :src="msg.content"
                class="cs-image"
                @click="previewImage(msg.content)"
              />
              <span v-else class="cs-text" :class="{ 'is-emoji': isEmojiOnly(msg.content) }">{{ msg.content }}</span>
            </div>
            <div class="cs-time">{{ formatTime(msg.createTime) }}</div>
          </div>
          </template>
        </div>
        <footer ref="csFooter" class="cs-footer">
          <div v-if="emojiOpen" class="cs-emoji-panel">
            <div class="cs-emoji-tabs">
              <div
                v-for="(group, idx) in emojiGroups"
                :key="group.id"
                class="cs-emoji-tab"
                :class="{ active: emojiGroup === idx }"
                role="button"
                @mousedown.prevent
                @click="emojiGroup = idx"
              >{{ group.icon }}</div>
            </div>
            <div class="cs-emoji-grid">
              <span
                v-for="em in emojiGroups[emojiGroup].emojis"
                :key="em"
                class="cs-emoji-cell"
                role="button"
                @mousedown.prevent
                @click="insertEmoji(em)"
              >{{ em }}</span>
            </div>
          </div>
          <div class="cs-composer">
            <div
              class="cs-emoji-btn"
              role="button"
              :class="{ active: emojiOpen }"
              :aria-label="$t('customerService.emoji')"
              :title="$t('customerService.emoji')"
              @mousedown.prevent
              @click="toggleEmoji"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="1.4"/>
                <path d="M7.2 12.15c.85 1.05 1.85 1.55 2.8 1.55s1.95-.5 2.8-1.55" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
                <circle cx="7.55" cy="8.35" r="0.95" fill="currentColor"/>
                <circle cx="12.45" cy="8.35" r="0.95" fill="currentColor"/>
              </svg>
            </div>
            <label
              class="cs-attach"
              :aria-label="$t('customerService.sendImage')"
              :title="$t('customerService.sendImage')"
            >
              <input type="file" accept="image/*" class="cs-file" @change="onImagePick" />
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <rect x="3" y="4.5" width="14" height="11" rx="2.2" stroke="currentColor" stroke-width="1.4"/>
                <circle cx="7.4" cy="8.2" r="1.15" stroke="currentColor" stroke-width="1.4"/>
                <path d="M3.6 13.4L7.4 10.6L10.6 13L16.4 8.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </label>
            <textarea
              ref="draftInput"
              v-model="draft"
              class="cs-input"
              rows="1"
              :placeholder="$t('customerService.placeholder')"
              @keydown.enter.exact.prevent="sendText"
            />
            <div
              class="cs-send"
              role="button"
              :class="{ 'is-ready': canSend }"
              :aria-label="$t('customerService.send')"
              :aria-disabled="canSend ? 'false' : 'true'"
              @click="sendText"
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M3.4 9.8L16.6 3.8L12.8 16.4L10.1 11.4L3.4 9.8Z" stroke="currentColor" stroke-width="1.45" stroke-linejoin="round"/>
                <path d="M10.1 11.4L16.6 3.8" stroke="currentColor" stroke-width="1.45" stroke-linecap="round"/>
              </svg>
            </div>
          </div>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex'
import { csBus } from '@/utils/csBus'
import { getToken } from '@/utils/auth'
import { VUE_APP_WS_URL } from '@/config'
import { getCsSession, getCsMessages, sendCsMessage, markCsRead } from '@/api/cs'
import { CHAT_EMOJI_GROUPS } from '@/utils/chatEmojis'
import request from '@/utils/request'
import Compressor from 'compressorjs'

export default {
  name: 'CustomerChat',
  data() {
    return {
      visible: false,
      session: null,
      messages: [],
      draft: '',
      sending: false,
      loadingMore: false,
      pageLoading: false,
      ws: null,
      pingTimer: null,
      reconnectTimer: null,
      emojiOpen: false,
      emojiGroup: 0,
      emojiGroups: CHAT_EMOJI_GROUPS
    }
  },
  computed: {
    ...mapState(['isPc']),
    canSend() {
      return !this.sending && !!(this.draft || '').trim()
    }
  },
  watch: {
    visible(val) {
      if (this.isPc) return
      document.body.style.overflow = val ? 'hidden' : ''
    }
  },
  mounted() {
    csBus.$on('open', this.open)
  },
  beforeDestroy() {
    csBus.$off('open', this.open)
    this.teardownWs()
    this.closeEmoji()
    document.body.style.overflow = ''
  },
  methods: {
    onRootClick() {
      if (!this.isPc) this.close()
    },
    async open() {
      this.visible = true
      this.pageLoading = true
      try {
        const res = await getCsSession()
        if (res.code !== 200 && res.code !== 0) {
          throw new Error(res.message || 'session error')
        }
        this.session = res.result
        await this.loadMessages()
        this.connectWs()
        this.$nextTick(() => this.scrollBottom())
        if (this.session && this.session.id) {
          markCsRead({ sessionId: this.session.id })
          this.$store.commit('SET_UNREAD_BADGES', { hasCsUnread: false })
        }
      } catch (e) {
        console.error(e)
        this.$message.error(this.$t('customerService.error'))
        this.visible = false
      } finally {
        this.pageLoading = false
      }
    },
    close() {
      this.visible = false
      this.closeEmoji()
      this.teardownWs()
    },
    toggleEmoji() {
      this.emojiOpen = !this.emojiOpen
      if (this.emojiOpen) {
        this.$nextTick(() => document.addEventListener('mousedown', this.onDocCloseEmoji))
      } else {
        document.removeEventListener('mousedown', this.onDocCloseEmoji)
      }
    },
    closeEmoji() {
      this.emojiOpen = false
      document.removeEventListener('mousedown', this.onDocCloseEmoji)
    },
    onDocCloseEmoji(e) {
      const footer = this.$refs.csFooter
      if (footer && footer.contains(e.target)) return
      this.closeEmoji()
    },
    insertEmoji(emoji) {
      const el = this.$refs.draftInput
      const text = this.draft || ''
      let start = text.length
      let end = text.length
      if (el && typeof el.selectionStart === 'number') {
        start = el.selectionStart
        end = el.selectionEnd
      }
      this.draft = text.slice(0, start) + emoji + text.slice(end)
      this.$nextTick(() => {
        if (!el || !el.setSelectionRange) return
        const pos = start + emoji.length
        el.focus()
        el.setSelectionRange(pos, pos)
      })
    },
    isEmojiOnly(text) {
      const t = String(text || '').trim()
      if (!t || t.length > 24) return false
      return !/[A-Za-z0-9\u4e00-\u9fff]/.test(t)
    },
    async loadMessages(beforeId) {
      if (!this.session) return
      this.loadingMore = !!beforeId
      try {
        const res = await getCsMessages({
          sessionId: this.session.id,
          beforeId,
          limit: 50
        })
        const list = (res.result || [])
        if (beforeId) {
          this.messages = [...list, ...this.messages]
        } else {
          this.messages = list
        }
      } finally {
        this.loadingMore = false
      }
    },
    connectWs() {
      this.teardownWs()
      const token = getToken()
      if (!token) return
      const base = (VUE_APP_WS_URL || '').replace(/\/$/, '')
      const url = `${base}/cs/user?token=${encodeURIComponent(token)}`
      this.ws = new WebSocket(url)
      this.ws.onmessage = (ev) => this.onWsMessage(ev.data)
      this.ws.onclose = () => this.scheduleReconnect()
      this.pingTimer = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ action: 'ping' }))
        }
      }, 25000)
    },
    scheduleReconnect() {
      if (!this.visible) return
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = setTimeout(() => this.connectWs(), 3000)
    },
    teardownWs() {
      clearInterval(this.pingTimer)
      clearTimeout(this.reconnectTimer)
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }
    },
    onWsMessage(raw) {
      try {
        const envelope = JSON.parse(raw)
        if (envelope.event === 'message' && envelope.data) {
          const msg = envelope.data
          if (msg.sessionId === this.session.id) {
            if (!this.messages.find((m) => m.id === msg.id)) {
              this.messages.push(msg)
              this.$nextTick(() => this.scrollBottom())
            }
            if (msg.senderType === 2) {
              markCsRead({ sessionId: this.session.id })
              this.$store.commit('SET_UNREAD_BADGES', { hasCsUnread: false })
            }
          }
        }
        if (envelope.event === 'message_delete' && envelope.data) {
          const id = envelope.data.messageId
          if (id != null) {
            this.messages = this.messages.filter((m) => String(m.id) !== String(id))
          }
        }
      } catch (e) {
        console.warn('cs ws parse', e)
      }
    },
    async sendText() {
      const text = (this.draft || '').trim()
      if (!text || !this.session) return
      this.draft = ''
      this.closeEmoji()
      await this.sendPayload('text', text)
    },
    async sendPayload(msgType, content) {
      if (!this.session || this.sending) return
      this.sending = true
      try {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(
            JSON.stringify({
              action: 'send',
              sessionId: this.session.id,
              msgType,
              content
            })
          )
        } else {
          const res = await sendCsMessage({
            sessionId: this.session.id,
            msgType,
            content
          })
          if (res.result) {
            this.appendLocal(res.result)
          }
        }
      } catch (e) {
        this.$message.error(this.$t('customerService.error'))
      } finally {
        this.sending = false
      }
    },
    appendLocal(msg) {
      if (!this.messages.find((m) => m.id === msg.id)) {
        this.messages.push(msg)
        this.$nextTick(() => this.scrollBottom())
      }
    },
    onImagePick(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      new Compressor(file, {
        quality: 0.8,
        maxWidth: 1280,
        success: (blob) => this.uploadImage(blob),
        error: () => this.uploadImage(file)
      })
    },
    async uploadImage(fileOrBlob) {
      const form = new FormData()
      form.append('file', fileOrBlob, 'cs.jpg')
      try {
        const res = await request({
          url: '/swx/customer/user/upload',
          method: 'post',
          data: form,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const url = res.url || (res.result && res.result.url)
        if (url) {
          await this.sendPayload('image', url)
        }
      } catch (e) {
        this.$message.error(this.$t('customerService.error'))
      }
    },
    previewImage(url) {
      window.open(url, '_blank')
    },
    scrollBottom() {
      const el = this.$refs.messageList
      if (el) el.scrollTop = el.scrollHeight
    },
    formatTime(t) {
      const text = this.$ldTime(t)
      if (!text || text === '--') return ''
      return text.slice(11, 16)
    }
  }
}
</script>

<style lang="scss" scoped>
.cs-root {
  position: fixed;
  z-index: 10000;
}
.cs-root--pc {
  right: 24px;
  bottom: 24px;
  width: 380px;
  height: 520px;
}
.cs-root--mobile {
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: var(--ld-overlay, rgba(17, 17, 17, 0.36));
  box-sizing: border-box;
}
.cs-panel {
  display: flex;
  flex-direction: column;
  background: var(--ld-bg-card, #fff);
  color: var(--ld-text, #111);
  border: 1px solid var(--ld-border, #ececec);
  box-shadow: var(--ld-shadow, 0 16px 48px rgba(0, 0, 0, 0.12));
  overflow: hidden;
}
.cs-root--pc .cs-panel {
  width: 100%;
  height: 100%;
  border-radius: 16px;
}
.cs-root--mobile .cs-panel {
  width: 100%;
  height: 80%;
  max-height: 80%;
  border: none;
  border-radius: 0.2rem 0.2rem 0 0;
  box-shadow: none;
  animation: cs-sheet-up 0.22s ease-out;
}
.sheet-handle {
  width: 0.36rem;
  height: 0.04rem;
  border-radius: 999px;
  background: var(--ld-handle, #d8d8d8);
  margin: 0.06rem auto 0.08rem;
  flex-shrink: 0;
}
.cs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--ld-bg-card, #fff);
  color: var(--ld-text, #111);
  border-bottom: 1px solid var(--ld-border, #ececec);
  flex-shrink: 0;
}
.cs-root--mobile .cs-header {
  justify-content: center;
  padding: 0.04rem 0.16rem 0.12rem;
  min-height: auto;
}
.cs-title {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--ld-text, #111);
}
.cs-root--mobile .cs-title {
  font-size: 0.16rem;
  text-align: center;
}
.cs-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: var(--ld-bg-muted, #f5f5f5);
  color: var(--ld-text-secondary, #666);
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  flex-shrink: 0;
  font-family: inherit;
  &:hover {
    color: var(--ld-text, #111);
    background: var(--ld-bg-active, #f2f2f2);
  }
  &:active {
    background: var(--ld-bg-active, #ececec) !important;
    border-radius: 50% !important;
  }
}
.cs-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  background: var(--ld-bg, #fff);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
.cs-loading {
  text-align: center;
  font-size: 12px;
  color: var(--ld-text-tertiary, #999);
  margin-bottom: 8px;
}
.cs-msg-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
  max-width: 78%;
  width: fit-content;
}
.cs-msg-row--mine {
  align-self: flex-end;
  align-items: flex-end;
  margin-left: auto;
}
.cs-msg-row--other {
  align-self: flex-start;
  align-items: flex-start;
  margin-right: auto;
}
.cs-bubble {
  padding: 10px 12px;
  border-radius: 12px;
  word-break: break-word;
}
.cs-msg-row--mine .cs-bubble {
  background: var(--ld-chat-mine-bg, #2563eb);
  color: var(--ld-chat-mine-text, #fff);
  border: 1px solid transparent;
}
.cs-msg-row--other .cs-bubble {
  background: var(--ld-chat-other-bg, #eef2ff);
  color: var(--ld-chat-other-text, #1e293b);
  border: 1px solid transparent;
}
.cs-text {
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
}
.cs-text.is-emoji {
  font-size: 28px;
  line-height: 1.25;
}
.cs-image {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  cursor: pointer;
  display: block;
}
.cs-time {
  font-size: 11px;
  color: var(--ld-text-tertiary, #999);
  margin-top: 8px;
  line-height: 1.2;
}
.cs-footer {
  padding: 10px 12px;
  padding-bottom: calc(10px + var(--ld-safe-bottom));
  border-top: 1px solid var(--ld-border, #ececec);
  background: var(--ld-bg-card, #fff);
  flex-shrink: 0;
}
.cs-emoji-panel {
  margin-bottom: 8px;
  border: 1px solid var(--ld-border, #ececec);
  border-radius: 16px;
  background: var(--ld-bg-card, #fff);
  overflow: hidden;
}
.cs-emoji-tabs {
  display: flex;
  gap: 4px;
  padding: 8px 8px 0;
  border-bottom: 1px solid var(--ld-border, #ececec);
}
.cs-emoji-tab {
  width: 36px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px 10px 0 0;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &.active {
    background: var(--ld-bg-active, #f2f2f2);
  }
}
.cs-emoji-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 2px;
  max-height: 196px;
  overflow-y: auto;
  padding: 8px;
}
.cs-emoji-cell {
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &:hover,
  &:active {
    background: var(--ld-bg-active, #f2f2f2);
  }
}
.cs-emoji-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  color: var(--ld-text-secondary, #888);
  background: transparent;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover,
  &.active {
    color: var(--ld-text, #111);
    background: var(--ld-bg-active, #f2f2f2);
  }
}
.cs-composer {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  min-height: 48px;
  padding: 6px 6px 6px 8px;
  border: 1px solid var(--ld-border, #ececec);
  border-radius: 24px;
  background: var(--ld-bg-muted, #f7f7f7);
  transition: border-color 0.15s ease, background 0.15s ease;
  &:focus-within {
    border-color: var(--ld-accent, #5347e0);
    background: var(--ld-bg-card, #fff);
    box-shadow: 0 0 0 3px var(--ld-accent-muted, #eef0ff);
  }
}
.cs-attach {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  color: var(--ld-text-secondary, #888);
  background: transparent;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover {
    color: var(--ld-text, #111);
    background: var(--ld-bg-active, #f2f2f2);
  }
  &:active {
    outline: none;
    box-shadow: none;
    transform: none;
    background: var(--ld-bg-active, #f2f2f2);
  }
}
.cs-file {
  display: none;
}
.cs-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 6px;
  min-height: 36px;
  max-height: 96px;
  resize: none;
  font-size: 14px;
  line-height: 1.45;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
  color: var(--ld-text, #111);
  -webkit-tap-highlight-color: transparent;
  &::placeholder {
    color: var(--ld-text-tertiary, #999);
  }
  &:focus,
  &:active {
    outline: none;
    box-shadow: none;
    background: transparent !important;
  }
}
.cs-send {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: default;
  color: var(--ld-text-tertiary, #999);
  background: var(--ld-bg-active, #f2f2f2);
  -webkit-tap-highlight-color: transparent;
  transition: background 0.18s ease, color 0.18s ease, opacity 0.15s ease;
  svg {
    display: block;
    margin-left: 1px;
  }
  &.is-ready {
    cursor: pointer;
    background: var(--ld-chat-mine-bg, #2563eb);
    color: var(--ld-chat-mine-text, #fff);
    &:hover {
      opacity: 0.92;
    }
    &:active {
      outline: none;
      box-shadow: none;
      transform: none;
      opacity: 0.88;
      background: var(--ld-chat-mine-bg, #2563eb);
    }
  }
}
.cs-fade-enter-active,
.cs-fade-leave-active {
  transition: opacity 0.2s ease;
}
.cs-fade-enter,
.cs-fade-leave-to {
  opacity: 0;
}
@keyframes cs-sheet-up {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
</style>
